import React from 'react';
import { setMonth, startOfMonth, setYear, startOfYear } from 'date-fns';
import {
  makeStyles,
  createStyles,
  Box,
  IconButton,
  Select,
  MenuItem,
} from '@material-ui/core';
import { colors } from 'design-system';
import {
  CaptionProps,
  useDayPicker,
  MonthChangeEventHandler,
  useNavigation,
} from 'react-day-picker';
import {
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@material-ui/icons';

const useStyles = makeStyles(() =>
  createStyles({
    selectMaterialUI: {
      textTransform: 'capitalize',
      '&::before': {
        borderBottom: 0,
      },
    },
    icons: {
      color: colors.bright_turquoise,
    },
    navSelectMonthYear: {
      marginLeft: '30px',
      marginRight: '30px',
    },
  }),
);

function createYearList(fromYear: number, toYear: number): Date[] {
  const years: Date[] = [];

  for (let year = fromYear; year <= toYear; year += 1) {
    years.push(setYear(startOfYear(new Date()), year));
  }
  return years;
}

function createMonthListt(): Date[] {
  const dropdownMonths: Date[] = [];
  // display all the 12 months
  const date = startOfMonth(new Date()); // Any date should be OK, as we just need the year
  for (let month = 0; month <= 11; month += 1) {
    dropdownMonths.push(setMonth(date, month));
  }
  return dropdownMonths;
}

const Caption = ({
  id,
  displayMonth,
}: CaptionProps): React.ReactElement | null => {
  const classes = useStyles();

  const {
    fromDate,
    toDate,
    locale,
    onMonthChange,
    formatters: { formatYearCaption, formatMonthCaption },
  } = useDayPicker();

  const { goToMonth, previousMonth, nextMonth } = useNavigation();

  const handleDateChange: MonthChangeEventHandler = (newDate) => {
    goToMonth(newDate);
    onMonthChange?.(newDate);
  };

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedMonth = Number(event.target.value);
    const newDate = setMonth(startOfMonth(displayMonth), selectedMonth);
    handleDateChange(newDate);
  };

  const handleYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedYear = Number(event.target.value);
    const newDate = setYear(startOfMonth(displayMonth), selectedYear);
    handleDateChange(newDate);
  };

  const handleGoToPreviousMonth = (): void => {
    if (!previousMonth) {
      return;
    }
    goToMonth(previousMonth);
    onMonthChange?.(previousMonth);
  };

  const handleGoToNextMonth = (): void => {
    if (!nextMonth) {
      return;
    }
    goToMonth(nextMonth);
    onMonthChange?.(nextMonth);
  };

  // Dropdown should appear only when both from/toDate is set
  if (!fromDate) {
    return <></>;
  }
  if (!toDate) {
    return <></>;
  }

  const fromYear = fromDate.getFullYear();
  const toYear = toDate.getFullYear();

  return (
    <Box
      id={id}
      display="flex"
      justifyContent="space-between"
      className={classes.navSelectMonthYear}
    >
      <IconButton
        id="sales-web-button-back-month"
        onClick={handleGoToPreviousMonth}
        disabled={!previousMonth}
        className={classes.icons}
        data-testid="back-button"
      >
        <NavigateBeforeIcon />
      </IconButton>
      <Select
        id="sales-web-select-month"
        data-testid="select-month"
        value={displayMonth.getMonth()}
        onChange={handleMonthChange}
        className={classes.selectMaterialUI}
      >
        {createMonthListt().map((month: Date) => (
          <MenuItem key={month.getMonth()} value={month.getMonth()}>
            {formatMonthCaption(month, { locale })}
          </MenuItem>
        ))}
      </Select>
      <Select
        id="sales-web-select-year"
        data-testid="select-year"
        value={displayMonth.getFullYear()}
        onChange={handleYearChange}
        className={classes.selectMaterialUI}
      >
        {createYearList(fromYear, toYear).map((year: Date) => (
          <MenuItem key={year.getFullYear()} value={year.getFullYear()}>
            {formatYearCaption(year, { locale })}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        id="sales-web-button-next-month"
        onClick={handleGoToNextMonth}
        disabled={!nextMonth}
        className={classes.icons}
        data-testid="next-button"
      >
        <NavigateNextIcon />
      </IconButton>
    </Box>
  );
};

export default Caption;
